import { useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { KeyRound, ShieldCheck, ScrollText, Users, Plus, Trash2, Pencil, X, Check, Lock, Unlock } from 'lucide-react';
import type { CredentialKind, CredentialSummary, ProposalStatus } from '@cortex/shared';
import { Api, type ToolInfo } from '../../lib/api';
import { useUi } from '../../store/ui';
import { useSessionContext } from '../../hooks/sessionContext';
import { JobsTab } from './JobsTab';
import { UsageTab } from './UsageTab';
import { BarChart3 } from 'lucide-react';
import { CalendarClock } from 'lucide-react';

type Tab = 'credentials' | 'policy' | 'audit' | 'users' | 'jobs' | 'usage';

export function AdminView() {
  const [tab, setTab] = useState<Tab>('credentials');
  const session = useSessionContext();
  if (session.user?.role !== 'ADMIN') return <div className="pane-empty">Admin is for ADMIN only.</div>;
  return (
    <div className="admin-view">
      <div className="admin-tabs">
        <button className={tab === 'credentials' ? 'on' : ''} onClick={() => setTab('credentials')}><KeyRound size={14} /> Credentials</button>
        <button className={tab === 'policy' ? 'on' : ''} onClick={() => setTab('policy')}><ShieldCheck size={14} /> Tool policy</button>
        <button className={tab === 'audit' ? 'on' : ''} onClick={() => setTab('audit')}><ScrollText size={14} /> Audit</button>
        <button className={tab === 'users' ? 'on' : ''} onClick={() => setTab('users')}><Users size={14} /> Users</button>
        <button className={tab === 'jobs' ? 'on' : ''} onClick={() => setTab('jobs')}><CalendarClock size={14} /> Jobs & watchers</button>
        <button className={tab === 'usage' ? 'on' : ''} onClick={() => setTab('usage')}><BarChart3 size={14} /> Usage</button>
      </div>
      <div className="admin-body">
        {tab === 'credentials' && <CredentialsTab />}
        {tab === 'policy' && <PolicyTab />}
        {tab === 'audit' && <AuditTab />}
        {tab === 'users' && <UsersTab />}
        {tab === 'jobs' && <JobsTab />}
        {tab === 'usage' && <UsageTab />}
      </div>
    </div>
  );
}

// ---------------------------------------------------------------- credentials

const KINDS: Array<{ key: CredentialKind; label: string; fields: Array<{ key: string; label: string; secret?: boolean; multiline?: boolean; options?: string[] }> }> = [
  { key: 'ssh_password', label: 'SSH password', fields: [{ key: 'password', label: 'Password', secret: true }] },
  { key: 'ssh_key', label: 'SSH private key', fields: [{ key: 'privateKey', label: 'Private key (PEM)', secret: true, multiline: true }, { key: 'passphrase', label: 'Key passphrase (optional)', secret: true }] },
  { key: 'snmp_v2c', label: 'SNMP v2c community', fields: [{ key: 'community', label: 'Community', secret: true }] },
  { key: 'snmp_v3', label: 'SNMP v3 user', fields: [{ key: 'securityLevel', label: 'Security level', options: ['authPriv', 'authNoPriv', 'noAuthNoPriv'] }, { key: 'authProtocol', label: 'Auth protocol', options: ['sha', 'sha256', 'sha512', 'md5'] }, { key: 'authKey', label: 'Auth key', secret: true }, { key: 'privProtocol', label: 'Priv protocol', options: ['aes', 'aes256b', 'aes256r', 'des'] }, { key: 'privKey', label: 'Priv key', secret: true }] },
  { key: 'api_token', label: 'API token', fields: [{ key: 'token', label: 'Token', secret: true }] },
  { key: 'basic', label: 'Service user (username + password)', fields: [{ key: 'password', label: 'Password', secret: true }] },
];

function CredentialsTab() {
  const qc = useQueryClient();
  const toast = useUi((s) => s.toast);
  const q = useQuery({ queryKey: ['credentials'], queryFn: Api.credentials.list, staleTime: 10_000 });
  const unused = useQuery({ queryKey: ['credentials-unused'], queryFn: () => Api.credentials.unused(90), staleTime: 60_000 });
  const [editing, setEditing] = useState<CredentialSummary | 'new' | null>(null);
  const refresh = () => (qc.invalidateQueries({ queryKey: ['credentials'] }), qc.invalidateQueries({ queryKey: ['credentials-unused'] }));
  const unusedIds = new Set((unused.data?.credentials ?? []).map((c) => c.id));
  const vault = q.data?.vault;
  return (
    <div>
      <div className="admin-head">
        {vault && (
          <span className={`chip ${vault.unlocked ? 'ok' : 'crit'}`} title={vault.unlocked ? 'AES-256-GCM, key from CORTEX_MASTER_KEY / passphrase' : 'Set CORTEX_MASTER_KEY or CORTEX_MASTER_PASSPHRASE in the API env'}>
            {vault.unlocked ? <Unlock size={11} /> : <Lock size={11} />} vault {vault.unlocked ? 'unlocked' : 'LOCKED'} · {vault.count}
          </span>
        )}
        <span className="muted" style={{ fontSize: 12 }}>Secrets are encrypted at rest, decrypted only inside a tool call, and never shown again. Scope every credential to a CIDR or host pattern.</span>
        <span className="spacer" />
        <button className="primary" onClick={() => setEditing('new')} disabled={!vault?.unlocked}><Plus size={12} /> Add credential</button>
      </div>
      <table className="admin-table">
        <thead><tr><th>Label</th><th>Kind</th><th>Targets</th><th>Min role</th><th>User</th><th>Last used</th><th></th></tr></thead>
        <tbody>
          {(q.data?.credentials ?? []).map((c) => (
            <tr key={c.id} className={unusedIds.has(c.id) ? 'dim' : ''}>
              <td><b>{c.label}</b>{c.notes && <div className="faint" style={{ fontSize: 11 }}>{c.notes}</div>}</td>
              <td className="mono">{c.kind}</td>
              <td className="mono">{c.targetGlob}</td>
              <td>{c.minRole}</td>
              <td className="mono">{c.username ?? ''}</td>
              <td className="mono" title={unusedIds.has(c.id) ? 'unused in 90 days' : ''}>{c.lastUsedAt ? new Date(c.lastUsedAt).toLocaleString() : 'never'}</td>
              <td style={{ whiteSpace: 'nowrap' }}>
                <button className="icon" title="Edit" onClick={() => setEditing(c)}><Pencil size={13} /></button>
                <button className="icon danger" title="Delete" onClick={() => window.confirm(`Delete credential "${c.label}"?`) && void Api.credentials.remove(c.id).then(() => (toast(`Deleted ${c.label}`), refresh()))}><Trash2 size={13} /></button>
              </td>
            </tr>
          ))}
          {q.data?.credentials.length === 0 && <tr><td colSpan={7} className="faint">No credentials yet.</td></tr>}
        </tbody>
      </table>
      {editing && <CredentialDialog existing={editing === 'new' ? null : editing} onClose={(changed) => (setEditing(null), changed && refresh())} />}
    </div>
  );
}

function CredentialDialog({ existing, onClose }: { existing: CredentialSummary | null; onClose: (changed: boolean) => void }) {
  const toast = useUi((s) => s.toast);
  const [kind, setKind] = useState<CredentialKind>(existing?.kind ?? 'ssh_password');
  const [label, setLabel] = useState(existing?.label ?? '');
  const [targetGlob, setTargetGlob] = useState(existing?.targetGlob ?? '10.42.0.0/16');
  const [minRole, setMinRole] = useState<'ADMIN' | 'ENGINEER' | 'VIEWER'>(existing?.minRole ?? 'ENGINEER');
  const [username, setUsername] = useState(existing?.username ?? '');
  const [notes, setNotes] = useState(existing?.notes ?? '');
  const [secret, setSecret] = useState<Record<string, string>>({ securityLevel: 'authPriv', authProtocol: 'sha', privProtocol: 'aes' });
  const [busy, setBusy] = useState(false);
  const def = KINDS.find((k) => k.key === kind)!;
  const secretGiven = def.fields.some((f) => f.secret && (secret[f.key] ?? '').length > 0);
  const save = async () => {
    setBusy(true);
    try {
      const secretPayload = { kind, ...Object.fromEntries(def.fields.map((f) => [f.key, secret[f.key] ?? undefined]).filter(([, v]) => v !== undefined && v !== '')) } as never;
      if (existing) await Api.credentials.update(existing.id, { label, targetGlob, minRole, username: username || null, notes: notes || null, ...(secretGiven ? { secret: secretPayload } : {}) });
      else await Api.credentials.create({ label, kind, targetGlob, minRole, username: username || undefined, notes: notes || undefined, secret: secretPayload });
      toast(existing ? `Updated ${label}` : `Added ${label}`);
      onClose(true);
    } catch (err) {
      toast((err as Error).message, 'crit');
    } finally {
      setBusy(false);
    }
  };
  return (
    <div className="scrim" onMouseDown={(e) => e.target === e.currentTarget && onClose(false)}>
      <div className="dialog" style={{ width: 560 }}>
        <div className="dialog-head">{existing ? `Edit ${existing.label}` : 'New credential'}<span className="spacer" /><button className="icon" onClick={() => onClose(false)}><X size={14} /></button></div>
        <div className="dialog-body">
          <div className="grid2">
            <label>Label<input type="text" value={label} onChange={(e) => setLabel(e.target.value)} placeholder="e.g. site-ssh-cambium" /></label>
            <label>Kind<select value={kind} disabled={!!existing} onChange={(e) => setKind(e.target.value as CredentialKind)}>{KINDS.map((k) => <option key={k.key} value={k.key}>{k.label}</option>)}</select></label>
            <label>Targets (CIDRs / host globs, comma-separated)<input type="text" value={targetGlob} onChange={(e) => setTargetGlob(e.target.value)} placeholder="10.42.0.0/16, *.site.internal" /></label>
            <label>Minimum role<select value={minRole} onChange={(e) => setMinRole(e.target.value as 'ADMIN')}><option>ADMIN</option><option>ENGINEER</option><option>VIEWER</option></select></label>
            <label>Username{kind === 'basic' ? ' (service user email)' : ''}<input type="text" value={username} onChange={(e) => setUsername(e.target.value)} /></label>
            <label>Notes<input type="text" value={notes} onChange={(e) => setNotes(e.target.value)} /></label>
          </div>
          <div className="label" style={{ marginTop: 4 }}>Secret {existing ? '(leave blank to keep the current one)' : ''}</div>
          <div className="grid2">
            {def.fields.map((f) => (
              <label key={f.key} style={f.multiline ? { gridColumn: '1 / -1' } : undefined}>
                {f.label}
                {f.options ? (
                  <select value={secret[f.key] ?? f.options[0]} onChange={(e) => setSecret({ ...secret, [f.key]: e.target.value })}>{f.options.map((o) => <option key={o}>{o}</option>)}</select>
                ) : f.multiline ? (
                  <textarea rows={6} value={secret[f.key] ?? ''} onChange={(e) => setSecret({ ...secret, [f.key]: e.target.value })} style={{ fontFamily: 'var(--font-mono)', fontSize: 11 }} />
                ) : (
                  <input type={f.secret ? 'password' : 'text'} autoComplete="new-password" value={secret[f.key] ?? ''} onChange={(e) => setSecret({ ...secret, [f.key]: e.target.value })} />
                )}
              </label>
            ))}
          </div>
        </div>
        <div className="dialog-foot">
          <button className="ghost" onClick={() => onClose(false)}>Cancel</button>
          <button className="primary" onClick={() => void save()} disabled={busy || !label || !targetGlob || (!existing && !secretGiven)}><Check size={12} /> {existing ? 'Save' : 'Add'}</button>
        </div>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------- policy

function PolicyTab() {
  const qc = useQueryClient();
  const toast = useUi((s) => s.toast);
  const q = useQuery({ queryKey: ['tools'], queryFn: Api.agent.tools, staleTime: 10_000 });
  const [edit, setEdit] = useState<ToolInfo | null>(null);
  const save = async (t: ToolInfo, patch: Partial<ToolInfo['policy']>) => {
    try {
      await Api.agent.setPolicy(t.name, patch);
      qc.invalidateQueries({ queryKey: ['tools'] });
    } catch (err) {
      toast((err as Error).message, 'crit');
    }
  };
  return (
    <div>
      <div className="admin-head"><span className="muted" style={{ fontSize: 12 }}>Per-tool gate. HIGH-risk tools need approval every call; net.ssh_run cannot have approval switched off and refuses to run without a target allowlist. The ssh denylist is code, not policy.</span></div>
      <table className="admin-table">
        <thead><tr><th>Tool</th><th>Risk</th><th>Enabled</th><th>Min role</th><th>Approval</th><th>Targets allow / deny</th><th>Rate</th><th></th></tr></thead>
        <tbody>
          {(q.data?.tools ?? []).map((t) => (
            <tr key={t.name} className={t.policy.enabled ? '' : 'dim'}>
              <td className="mono" title={t.description}>{t.name}</td>
              <td><span className={`chip ${t.riskLevel === 'SAFE' ? 'ok' : t.riskLevel === 'HIGH' ? 'crit' : t.riskLevel === 'MEDIUM' ? 'warn' : ''}`} style={{ fontSize: 9 }}>{t.riskLevel}</span></td>
              <td><input type="checkbox" checked={t.policy.enabled} onChange={(e) => void save(t, { enabled: e.target.checked })} /></td>
              <td><select value={t.policy.minRole} onChange={(e) => void save(t, { minRole: e.target.value as 'ADMIN' })}><option>VIEWER</option><option>ENGINEER</option><option>ADMIN</option></select></td>
              <td><input type="checkbox" checked={t.policy.requiresApproval || t.name === 'net.ssh_run'} disabled={t.name === 'net.ssh_run'} onChange={(e) => void save(t, { requiresApproval: e.target.checked })} /></td>
              <td className="mono" style={{ fontSize: 11 }}>
                {t.policy.targetAllow?.length ? <div>allow: {t.policy.targetAllow.join(', ')}</div> : <div className="faint">{t.name === 'net.ssh_run' ? 'allow: (none — ssh refused)' : 'allow: any'}</div>}
                {t.policy.targetDeny?.length ? <div>deny: {t.policy.targetDeny.join(', ')}</div> : null}
              </td>
              <td className="mono" style={{ fontSize: 11 }}>{t.policy.rateLimit ? `${t.policy.rateLimit.perMinute ?? '∞'}/min · ${t.policy.rateLimit.perSession ?? '∞'}/session` : '—'}</td>
              <td><button className="icon" onClick={() => setEdit(t)}><Pencil size={13} /></button></td>
            </tr>
          ))}
        </tbody>
      </table>
      {edit && <PolicyDialog tool={edit} onClose={() => setEdit(null)} onSave={(p) => void save(edit, p).then(() => setEdit(null))} />}
    </div>
  );
}

function PolicyDialog({ tool, onClose, onSave }: { tool: ToolInfo; onClose: () => void; onSave: (p: Partial<ToolInfo['policy']>) => void }) {
  const [allow, setAllow] = useState((tool.policy.targetAllow ?? []).join('\n'));
  const [deny, setDeny] = useState((tool.policy.targetDeny ?? []).join('\n'));
  const [perMinute, setPerMinute] = useState(String(tool.policy.rateLimit?.perMinute ?? ''));
  const [perSession, setPerSession] = useState(String(tool.policy.rateLimit?.perSession ?? ''));
  const lines = (s: string) => s.split(/\r?\n/).map((x) => x.trim()).filter(Boolean);
  return (
    <div className="scrim" onMouseDown={(e) => e.target === e.currentTarget && onClose()}>
      <div className="dialog" style={{ width: 520 }}>
        <div className="dialog-head"><span className="mono">{tool.name}</span><span className="spacer" /><button className="icon" onClick={onClose}><X size={14} /></button></div>
        <div className="dialog-body">
          <div className="muted" style={{ fontSize: 12 }}>{tool.description}</div>
          <label>Target allowlist (one CIDR / host glob per line{tool.name === 'net.ssh_run' ? '; required for ssh — management VLANs only' : '; empty = any'})<textarea rows={4} value={allow} onChange={(e) => setAllow(e.target.value)} style={{ fontFamily: 'var(--font-mono)' }} placeholder={'10.42.0.0/16\n*.cqtex.internal'} /></label>
          <label>Target denylist (one per line)<textarea rows={3} value={deny} onChange={(e) => setDeny(e.target.value)} style={{ fontFamily: 'var(--font-mono)' }} /></label>
          <div className="grid2">
            <label>Per minute<input type="number" value={perMinute} onChange={(e) => setPerMinute(e.target.value)} /></label>
            <label>Per session<input type="number" value={perSession} onChange={(e) => setPerSession(e.target.value)} /></label>
          </div>
        </div>
        <div className="dialog-foot">
          <button className="ghost" onClick={onClose}>Cancel</button>
          <button className="primary" onClick={() => onSave({ targetAllow: lines(allow).length ? lines(allow) : null, targetDeny: lines(deny).length ? lines(deny) : null, rateLimit: perMinute || perSession ? { ...(perMinute ? { perMinute: Number(perMinute) } : {}), ...(perSession ? { perSession: Number(perSession) } : {}) } : null })}><Check size={12} /> Save</button>
        </div>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------- audit

function AuditTab() {
  const [filters, setFilters] = useState<{ action: string; user: string; q: string; actor: '' | 'user' | 'agent' | 'system' }>({ action: '', user: '', q: '', actor: '' });
  const [before, setBefore] = useState<string | undefined>(undefined);
  const q = useQuery({ queryKey: ['audit', filters, before], queryFn: () => Api.admin.audit({ ...filters, actor: filters.actor || undefined, before, limit: 100 }), staleTime: 5_000 });
  return (
    <div>
      <div className="admin-head">
        <input type="text" placeholder="action contains…" value={filters.action} onChange={(e) => (setBefore(undefined), setFilters({ ...filters, action: e.target.value }))} list="audit-actions" style={{ width: 200 }} />
        <datalist id="audit-actions">{q.data?.actions.map((a) => <option key={a.action} value={a.action}>{a.count}</option>)}</datalist>
        <input type="text" placeholder="user email…" value={filters.user} onChange={(e) => (setBefore(undefined), setFilters({ ...filters, user: e.target.value }))} style={{ width: 180 }} />
        <input type="text" placeholder="detail contains…" value={filters.q} onChange={(e) => (setBefore(undefined), setFilters({ ...filters, q: e.target.value }))} style={{ width: 200 }} />
        <select value={filters.actor} onChange={(e) => (setBefore(undefined), setFilters({ ...filters, actor: e.target.value as 'user' }))}><option value="">any actor</option><option value="user">user</option><option value="agent">agent</option><option value="system">system</option></select>
        <span className="spacer" />
        <span className="faint" style={{ fontSize: 11 }}>append-only · never pruned</span>
      </div>
      <table className="admin-table audit">
        <thead><tr><th>When</th><th>Actor</th><th>User</th><th>Action</th><th>Target</th><th>Detail</th></tr></thead>
        <tbody>
          {(q.data?.entries ?? []).map((e) => (
            <tr key={e.id}>
              <td className="mono" style={{ whiteSpace: 'nowrap' }}>{new Date(e.at).toLocaleString()}</td>
              <td><span className={`chip ${e.actor === 'agent' ? 'teal' : ''}`} style={{ fontSize: 9 }}>{e.actor}</span></td>
              <td className="mono" style={{ fontSize: 11 }}>{e.user?.email ?? ''}</td>
              <td className="mono">{e.action}</td>
              <td className="mono faint" style={{ fontSize: 11, maxWidth: 160, overflow: 'hidden', textOverflow: 'ellipsis' }}>{e.target ?? ''}</td>
              <td className="mono" style={{ fontSize: 11, whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>{summariseDetail(e.detail)}</td>
            </tr>
          ))}
        </tbody>
      </table>
      {q.data?.nextBefore && <button className="ghost" style={{ margin: 10 }} onClick={() => setBefore(q.data!.nextBefore!)}>Older…</button>}
    </div>
  );
}

function summariseDetail(d: Record<string, unknown>): string {
  return Object.entries(d)
    .filter(([k]) => k !== 'path')
    .map(([k, v]) => `${k}=${typeof v === 'string' ? v : JSON.stringify(v)}`)
    .join('  ')
    .slice(0, 400);
}

// ---------------------------------------------------------------- users

function UsersTab() {
  const qc = useQueryClient();
  const session = useSessionContext();
  const q = useQuery({ queryKey: ['admin-users'], queryFn: Api.admin.users, staleTime: 10_000 });
  const update = async (id: string, p: { isActive?: boolean; role?: 'ADMIN' | 'ENGINEER' | 'VIEWER' }) => {
    await Api.admin.updateUser(id, p);
    qc.invalidateQueries({ queryKey: ['admin-users'] });
  };
  return (
    <div>
      <div className="admin-head"><span className="muted" style={{ fontSize: 12 }}>Users arrive from the Command Centre launch; roles are re-synced from the hub on every launch. Deactivating a user blocks them immediately.</span></div>
      <table className="admin-table">
        <thead><tr><th>Email</th><th>Name</th><th>Role</th><th>Active</th><th>Last seen</th></tr></thead>
        <tbody>
          {(q.data?.users ?? []).map((u) => (
            <tr key={u.id} className={u.isActive ? '' : 'dim'}>
              <td className="mono">{u.email}</td>
              <td>{u.name}</td>
              <td><select value={u.role} onChange={(e) => void update(u.id, { role: e.target.value as 'ADMIN' })}><option>ADMIN</option><option>ENGINEER</option><option>VIEWER</option></select></td>
              <td><input type="checkbox" checked={u.isActive} disabled={u.id === session.user?.id} onChange={(e) => void update(u.id, { isActive: e.target.checked })} /></td>
              <td className="mono">{new Date(u.lastSeenAt).toLocaleString()}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

export type { ProposalStatus };
